import type { OperationVariables, TypedDocumentNode } from '@apollo/client';
import { useSubscription } from '@apollo/client/react';
import type { DocumentNode } from 'graphql';
import { useEffect, useRef } from 'react';

import type { Undefinable } from '@zen/utils/typescript';

export const RESUBSCRIBE_DELAY_MS: number = 1000;

const useResilientSubscription = <TData, TVariables extends OperationVariables>(
  subscription: DocumentNode | TypedDocumentNode<TData, TVariables>,
  options: useSubscription.Options<TData, TVariables>
): useSubscription.Result<TData> => {
  const { onComplete } = options;
  const restartRef = useRef<() => void>(() => {});
  const resubscribeTimeoutRef = useRef<Undefinable<ReturnType<typeof setTimeout>>>(undefined);

  const handleComplete = (): void => {
    onComplete?.();
    clearTimeout(resubscribeTimeoutRef.current);
    resubscribeTimeoutRef.current = setTimeout((): void => restartRef.current(), RESUBSCRIBE_DELAY_MS);
  };

  const result: useSubscription.Result<TData> = useSubscription<TData, TVariables>(subscription, {
    ...options,
    onComplete: handleComplete
  });
  const { restart } = result;

  useEffect((): (() => void) => {
    restartRef.current = restart;

    return (): void => clearTimeout(resubscribeTimeoutRef.current);
  }, [restart]);

  return result;
};

export default useResilientSubscription;
